import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.user_type === 'staff') navigate('/admin', { replace: true });
      else if (user.user_type === 'client') navigate('/portal', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const loggedUser = await login(email, password);
      if (loggedUser.user_type === 'staff') navigate('/admin');
      else if (loggedUser.user_type === 'client') navigate('/portal');
      else navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error('Please enter your email'); return; }
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail.trim());
      setForgotSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
    setForgotLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="login-page">
      <Card className="w-full max-w-sm bg-white border-gray-200">
        <CardHeader className="text-center pb-4">
          <img src="https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png" alt="CTS" className="h-12 mx-auto mb-3" />
          <CardTitle className="text-lg text-gray-900">{forgotMode ? 'Reset Password' : 'Sign In'}</CardTitle>
          <p className="text-xs text-gray-400">
            {forgotMode ? 'Enter your email to receive a reset link' : 'Canadian Truck Sales Portal'}
          </p>
        </CardHeader>
        <CardContent>
          {!forgotMode ? (
            <>
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="bg-gray-100 border-gray-200 text-gray-900"
                  data-testid="login-email"
                  required
                />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="bg-gray-100 border-gray-200 text-gray-900"
                  data-testid="login-password"
                  required
                />
                <Button type="submit" className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" disabled={loading} data-testid="login-submit">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Signing in...</> : 'Sign In'}
                </Button>
              </form>
              <div className="text-center mt-3">
                <button
                  onClick={() => { setForgotMode(true); setForgotEmail(email); setForgotSent(false); }}
                  className="text-xs text-[#E31937] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </>
          ) : forgotSent ? (
            <div className="text-center py-4" data-testid="forgot-sent">
              <Mail className="w-10 h-10 text-[#E31937] mx-auto mb-3" />
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Check Your Email</h3>
              <p className="text-xs text-gray-500 mb-4">
                If an account exists for <strong>{forgotEmail}</strong>, we've sent a password reset link. Check your inbox and spam folder.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setForgotMode(false); setForgotSent(false); }}
                data-testid="back-to-login-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to Login
              </Button>
            </div>
          ) : (
            <>
              <form onSubmit={handleForgot} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  className="bg-gray-100 border-gray-200 text-gray-900"
                  data-testid="forgot-email"
                  required
                />
                <Button type="submit" className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" disabled={forgotLoading} data-testid="forgot-submit">
                  {forgotLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Sending...</> : 'Send Reset Link'}
                </Button>
              </form>
              <div className="text-center mt-3">
                <button
                  onClick={() => setForgotMode(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-3 h-3" /> Back to Sign In
                </button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
